import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { InfoCircle } from 'iconsax-react';

import {
  useGetSupportTicketDetailsQuery,
  useGetSupportTicketHistoryQuery,
  useCreateSupportTicketChatMutation,
  useAssignSupportTicketMutation,
} from '../../store/services/SupportService';
import {
  setSelectedRoomId,
  setSelectedAllUsers,
  setSelectedUser,
  setIsGrouping,
  setIsClosed,
  setCanCancelFriendlyStage,
} from '../../store/slices/chat';
import { userDataInChat } from '../../types/chat';
import { ChatHistoryModal } from '../Support/ChatHistoryModal';

import {
  TicketDetailsSkeleton,
  TicketInfo,
  TicketAttachments,
  TicketActivity,
  TicketHistory,
  TicketFooter,
  CloseTicketModal,
} from './components';

export const TicketDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const ticketId = id || null;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [selectedChatRoomId, setSelectedChatRoomId] = useState<string | null>(
    null
  );

  const { data, isLoading } = useGetSupportTicketDetailsQuery(
    { ticketId: ticketId! },
    { skip: !ticketId }
  );

  const { data: historyData, isLoading: isHistoryLoading } =
    useGetSupportTicketHistoryQuery(
      { ticketId: ticketId! },
      { skip: !ticketId }
    );

  const [createChat, { isLoading: isCreatingChat }] =
    useCreateSupportTicketChatMutation();
  const [assignSupportTicket] = useAssignSupportTicketMutation();

  const ticket = data?.payload;
  const historyTickets = historyData?.payload || [];

  const hasAttemptedAssign = useRef(false);

  // Auto-assign ticket if status is NEW
  useEffect(() => {
    if (!ticket?.id || hasAttemptedAssign.current) return;
    const isNew =
      ticket.status?.code === 'NEW' ||
      ticket.status?.name?.toUpperCase() === 'NEW';
    if (isNew) {
      hasAttemptedAssign.current = true;
      assignSupportTicket({ id: ticket.id })
        .unwrap()
        .catch((err) => {
          console.error('Failed to auto-assign ticket:', err);
        });
    }
  }, [ticket?.id, ticket?.status, assignSupportTicket]);

  const isTicketClosed =
    ticket?.status?.code === 'CLOSED' || ticket?.status?.code === 'RESOLVED';
  const canCloseTicket =
    !isTicketClosed && (ticket?.actions ? ticket.actions.canCloseTicket : true);
  const canOpenChat = ticket?.actions ? ticket.actions.canOpenChat : true;

  const handleCreateChat = async () => {
    if (!ticketId) return;
    try {
      const response = await createChat({ id: ticketId, ticketId }).unwrap();
      const chatData = response?.payload;
      if (chatData) {
        const roomId = chatData.chatRoomId || chatData.roomId;
        if (roomId) {
          dispatch(setSelectedRoomId(String(roomId)));
        }
        if (chatData.users && chatData.users.length > 0) {
          const mappedUsers: userDataInChat[] = chatData.users.map((u) => ({
            id: u.id,
            name: u.name,
            image: u.avatar || u.image || '',
            userBadge: null,
            completedJobs: 0,
          }));
          dispatch(setSelectedAllUsers(mappedUsers));
          const clientUser =
            chatData.users.find(
              (u) => u.role !== 'ADMIN' && u.role !== 'SUPPORT'
            ) || chatData.users[0];
          if (clientUser) {
            dispatch(
              setSelectedUser({
                ...clientUser,
                image: clientUser.avatar || clientUser.image || '',
              } as any)
            );
          }
        } else if (ticket) {
          const userEmail = ticket.requesterEmail || ticket.customerEmail;
          const userName = ticket.requesterName || ticket.customerName || userEmail || 'Customer';
          const userPhoto = ticket.requesterPhoto || ticket.customerPhoto || '';
          const fallbackUser: userDataInChat = {
            id: userEmail || ticket.id,
            name: userName,
            image: userPhoto,
            userBadge: null,
            completedJobs: 0,
          };
          dispatch(setSelectedAllUsers([fallbackUser]));
          dispatch(
            setSelectedUser({
              id: userEmail || ticket.id,
              name: userName,
              email: userEmail,
              image: userPhoto,
              role: 'CLIENT',
            } as any)
          );
        }
        dispatch(setIsGrouping(Boolean(chatData.isGrouping)));
        dispatch(setIsClosed(Boolean(chatData.isClosed)));
        if (chatData?.actionsDto?.canCancelFriendlyStage !== undefined) {
          dispatch(
            setCanCancelFriendlyStage(
              chatData.actionsDto.canCancelFriendlyStage
            )
          );
        }
        const targetRoomId = chatData?.chatRoomId || chatData?.roomId;
        navigate(
          targetRoomId ? `/messages?chatRoomId=${targetRoomId}` : '/messages'
        );
      } else {
        navigate('/messages');
      }
    } catch (error: any) {
      console.error('Failed to create chat:', error);
      message.error(
        error?.data?.message ||
          t('support.somethingWentWrong', 'Something went wrong')
      );
    }
  };

  const handleOpenChatHistory = (roomId: string) => {
    setSelectedChatRoomId(roomId);
    setIsChatModalOpen(true);
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-140px)] relative">
      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex-1">
          <TicketDetailsSkeleton />
        </div>
      ) : !ticket ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 flex flex-col items-center justify-center min-h-[350px] text-gray-500 flex-1">
          <InfoCircle size={48} className="text-gray-300 mb-3" />
          <p className="text-base font-medium">
            {t('support.ticketNotFound', 'Ticket details not found')}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col gap-6 flex-1 mb-6 min-w-0">
          <TicketInfo ticket={ticket} />
          <TicketAttachments documents={ticket.documentInfoDtos || []} />
          <TicketActivity
            activities={ticket.activityDtos || []}
            onOpenChatHistory={handleOpenChatHistory}
          />
          <TicketHistory
            historyTickets={historyTickets}
            isLoading={isHistoryLoading}
            onOpenChatHistory={handleOpenChatHistory}
          />
        </div>
      )}

      {/* Sticky Bottom Action Bar */}
      <div className="sticky bottom-0 -mx-6 -mb-6 mt-auto bg-white border-t border-gray-200 px-6 py-4 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-20">
        <TicketFooter
          isLoading={isLoading}
          hasTicket={Boolean(ticket)}
          canOpenChat={canOpenChat}
          canCloseTicket={canCloseTicket}
          isCreatingChat={isCreatingChat}
          onCreateChat={handleCreateChat}
          onOpenCloseModal={() => setIsCloseModalOpen(true)}
        />
      </div>

      {selectedChatRoomId && (
        <ChatHistoryModal
          roomId={selectedChatRoomId}
          isOpen={isChatModalOpen}
          onClose={() => {
            setIsChatModalOpen(false);
            setSelectedChatRoomId(null);
          }}
        />
      )}

      <CloseTicketModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        ticketId={ticketId}
      />
    </div>
  );
};

export default TicketDetailsPage;
